/**
 * Корзина. У гостя она лежит в браузере, у вошедшего — на сервере: только
 * сервер знает цены, наличие и что товар ещё продаётся.
 *
 * Поэтому гостевая корзина при входе не теряется, а доливается в серверную
 * (`POST /cart/merge`), и дальше правда одна — серверная.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { mediaUrl, type ApiClient, type components } from '@dorixona/api'
import { useApi } from '../../api/context'
import { keys } from '../../api/keys'
import { env } from '../../config/env'

type CartLineDto = components['schemas']['CartLine']

/** Больше 99 штук одной строки сервер не примет ни при каком остатке. */
export const CART_MAX_QTY = 99

export interface CartLine {
  productId: number
  name: string
  price: number
  qty: number
  total: number
  /** Процент кэшбэка строки — его считает сервер (R-12). */
  cb: number
  rx: boolean
  /** Товар сняли с витрины, пока он лежал в корзине. */
  active: boolean
  inStock: boolean
  /** Больше этой строки не взять: выбранная аптека или лучшая по сети (R-26). */
  maxQty: number
  photo: string
}

export type CartIssue = 'unavailable' | 'out_of_stock'

export interface Cart {
  lines: CartLine[]
  goods: number
  /** Кэшбэк, если заплатить всю сумму деньгами. */
  cashback: number
  hasRx: boolean
  /** Что мешает оформить: товар снят с витрины или кончился. */
  issues: CartIssue[]
}

export function toLine(dto: CartLineDto): CartLine {
  return {
    productId: dto.product_id ?? 0,
    name: dto.name ?? '',
    price: dto.price ?? 0,
    qty: dto.qty ?? 0,
    total: dto.line_total ?? 0,
    cb: dto.cashback_pct ?? 0,
    rx: dto.is_rx ?? false,
    active: dto.is_active ?? true,
    inStock: dto.in_stock ?? true,
    maxQty: dto.max_qty ?? CART_MAX_QTY,
    photo: mediaUrl(dto.photo ?? '', env.apiBaseUrl),
  }
}

interface CartDto {
  items?: CartLineDto[]
  goods_total?: number
  cashback?: number
  has_rx?: boolean
  issues?: CartIssue[]
}

export function toCart(dto: CartDto): Cart {
  return {
    lines: (dto.items ?? []).map(toLine),
    goods: dto.goods_total ?? 0,
    cashback: dto.cashback ?? 0,
    hasRx: dto.has_rx ?? false,
    issues: dto.issues ?? [],
  }
}

export const EMPTY_CART: Cart = {
  lines: [],
  goods: 0,
  cashback: 0,
  hasRx: false,
  issues: [],
}

export function fetchCart(api: ApiClient): Promise<Cart> {
  return api.get<CartDto>('/cart').then(toCart)
}

/** Корзина есть только у вошедшего: гостю сервер ответит 401. */
export function useCart(signedIn: boolean) {
  const api = useApi()
  return useQuery({ queryKey: keys.cart, queryFn: () => fetchCart(api), enabled: signedIn })
}

/**
 * Количество строки. Ответ в кэш кладёт `CartProvider`: при быстрых нажатиях
 * запросы обгоняют друг друга, и только он знает, какой ответ последний.
 */
export function useSetQuantity() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ productId, qty }: { productId: number; qty: number }) =>
      toCart(await api.put<CartDto>(`/cart/items/${productId}`, { qty })),
    onSuccess: () => client.invalidateQueries({ queryKey: ['checkout'] }),
  })
}

export function useClearCart() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => api.del<unknown>('/cart'),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: keys.cart })
      await client.invalidateQueries({ queryKey: ['checkout'] })
    },
  })
}

export interface GuestLine {
  productId: number
  qty: number
}

/**
 * Долить гостевую корзину в серверную после входа. Количества складываются,
 * незнакомые товары сервер пропускает — своего списка «что пропало» он не
 * возвращает, и придумывать его мы не станем.
 */
export function useMergeCart() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (lines: GuestLine[]) =>
      toCart(
        await api.post<CartDto>('/cart/merge', {
          items: lines.map((line) => ({ product_id: line.productId, qty: line.qty })),
        }),
      ),
    onSuccess: (cart) => {
      client.setQueryData(keys.cart, cart)
    },
  })
}
